/**
 * Shared data contracts between the web client and Netlify Functions.
 * Keep this file dependency-free: it is bundled into both.
 *
 * The same shapes are what a CRM adapter (KeyCRM) or a future
 * quotation engine receives, so treat changes here as API changes
 * and bump RFQ_SCHEMA_VERSION.
 */

export const RFQ_SCHEMA_VERSION = 3 as const;

/** v3: ceramic mugs replaced by round handleless drinkware (thermoses, sports bottles, tumblers). */
export type ProductKind = 'candle' | 'diffuser' | 'bottle' | 'drinkware' | 'cosmetic' | 'gift';
export const PRODUCT_KINDS: ProductKind[] = ['candle', 'diffuser', 'bottle', 'cosmetic', 'drinkware', 'gift'];

/** Production limits of the cylindrical printer, mm: body diameter and body length (without closure). */
export const PRINT_LIMITS = { diameterMm: [45, 160], lengthMm: [80, 280] } as const;

export type InkSetup = 'cmyk' | 'cmyk_white' | 'cmyk_white_varnish';
export type Coverage = 'single_logo' | 'partial' | 'full_wrap';
export type MaterialId =
  | 'clear'
  | 'amber'
  | 'black'
  | 'white'
  | 'frosted'
  | 'green'
  | 'cobalt'
  | 'steel'
  | 'powder_white'
  | 'powder_black'
  | 'powder_sage'
  | 'powder_navy';
/** @deprecated kept for older imports; use MaterialId. */
export type GlassFinish = MaterialId;

/** Placement of artwork on the unwrapped print surface, in millimetres. */
export interface ArtworkPlacement {
  /** Centre along the circumference, 0…circumference (wraps). */
  uMm: number;
  /** Centre from the top edge of the printable band. */
  vMm: number;
  widthMm: number;
  heightMm: number;
  rotationDeg: number;
}

export interface ArtworkInfo {
  /** Server id from /api/artwork, or null in offline/preview mode or when the upload failed. */
  artworkId: string | null;
  fileName: string;
  mime: string;
  sourceFormat: 'png' | 'jpeg' | 'svg' | 'pdf';
  sizeBytes: number;
  sha256: string;
  kind: 'raster' | 'vector';
  pixelWidth: number | null;
  pixelHeight: number | null;
  /** Effective resolution at the chosen print size; null for vector sources. */
  effectiveDpi: number | null;
  hasTransparency: boolean;
  pdfPages?: number;
  warnings: string[];
}

/** One decorated item: candle, diffuser, bottle/jar, cosmetic pack or drinkware. */
export interface ProductConfiguration {
  product: Exclude<ProductKind, 'gift'>;
  containerId: string;
  containerLabel: string;
  diameterMm: number;
  heightMm: number;
  volumeMl: number;
  printBand: { circumferenceMm: number; heightMm: number };
  material: MaterialId;
  closure: string | null;
  ink: InkSetup;
  coverage: Coverage;
  designHelpRequested: boolean;
  artwork: ArtworkInfo | null;
  placement: ArtworkPlacement | null;
  /** Share of the printable band covered by the artwork bounding box, 0…1. */
  coverageRatio: number | null;
  /** Server id of the rendered front mockup PNG, if uploaded. */
  mockupId: string | null;
}

/** @deprecated use ProductConfiguration */
export type CandleConfiguration = ProductConfiguration;

export type GiftPackaging = 'kraft' | 'black_box' | 'none';

export interface GiftItem {
  product: Exclude<ProductKind, 'gift'>;
  containerId: string;
  containerLabel: string;
  material: MaterialId;
  qtyPerSet: number;
}

/** Corporate gift set: several items decorated with one logo. */
export interface GiftSetConfiguration {
  product: 'gift';
  items: GiftItem[];
  ink: InkSetup;
  logo: ArtworkInfo | null;
  designHelpRequested: boolean;
  /** Logo height as a share of each item's print band. */
  logoScale: number;
  personalization: { mode: 'none' | 'names'; names: string[] };
  packaging: GiftPackaging;
  mockupId: string | null;
}

export type Configuration = ProductConfiguration | GiftSetConfiguration;

export interface RfqContact {
  company: string;
  contactPerson: string;
  email: string;
  phone: string;
}

export interface RfqOrder {
  quantity: number;
  skuCount: number;
  targetDate: string | null;
}

export interface RfqPayload {
  schemaVersion: typeof RFQ_SCHEMA_VERSION;
  kind: 'configurator' | 'general';
  locale: string;
  contact: RfqContact;
  order: RfqOrder | null;
  configuration: Configuration | null;
  /** For general enquiries from the contact section. */
  productInterest: string | null;
  comments: string;
  consent: boolean;
  /** Honeypot: must stay empty. */
  website?: string;
  clientCreatedAt: string;
}

export interface RfqRecord extends RfqPayload {
  rfqNumber: string;
  receivedAt: string;
  status: 'new' | 'in_review' | 'quoted' | 'won' | 'lost';
  crm?: { provider: string; externalId: string | null; syncedAt: string | null; error?: string };
}

export interface RfqSubmitResponse {
  ok: true;
  rfqNumber: string;
  receivedAt: string;
}

export interface ApiError {
  ok: false;
  error: string;
  fields?: Record<string, string>;
}

export const QUANTITY_PRESETS = [100, 250, 500, 1000, 2500, 5000] as const;
export const MAX_QUANTITY = 1_000_000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+()\-\s\d]{7,20}$/;

type Fields = Record<string, string>;

function str(v: unknown, max: number): string {
  return typeof v === 'string' ? v.trim().slice(0, max) : '';
}

/**
 * Validates and normalises an RFQ. Used by the form (instant feedback)
 * and by the function (authoritative). Error values are i18n keys.
 */
export function validateRfq(input: unknown): { ok: true; value: RfqPayload } | { ok: false; fields: Fields } {
  const fields: Fields = {};
  const o = (input ?? {}) as Record<string, any>;
  const c = (o.contact ?? {}) as Record<string, unknown>;

  const contact: RfqContact = {
    company: str(c.company, 160),
    contactPerson: str(c.contactPerson, 120),
    email: str(c.email, 160).toLowerCase(),
    phone: str(c.phone, 32),
  };
  if (contact.company.length < 2) fields.company = 'validation.required';
  if (contact.contactPerson.length < 2) fields.contactPerson = 'validation.required';
  if (!EMAIL_RE.test(contact.email)) fields.email = 'validation.email';
  if (contact.phone && !PHONE_RE.test(contact.phone)) fields.phone = 'validation.phone';
  if (!contact.phone) fields.phone = 'validation.required';

  const kind = o.kind === 'general' ? 'general' : 'configurator';

  let order: RfqOrder | null = null;
  if (kind === 'configurator') {
    const q = Math.round(Number(o.order?.quantity));
    const sku = Math.round(Number(o.order?.skuCount ?? 1));
    if (!Number.isFinite(q) || q < 1 || q > MAX_QUANTITY) fields.quantity = 'validation.quantity';
    order = {
      quantity: Number.isFinite(q) ? q : 0,
      skuCount: Number.isFinite(sku) && sku >= 1 ? Math.min(sku, 500) : 1,
      targetDate: typeof o.order?.targetDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.order.targetDate) ? o.order.targetDate : null,
    };
    const cfg = o.configuration as Record<string, any> | undefined;
    if (!cfg || !PRODUCT_KINDS.includes(cfg.product)) fields.configuration = 'validation.configuration';
    else if (cfg.product === 'gift') {
      if (!Array.isArray(cfg.items) || cfg.items.length === 0 || cfg.items.length > 12) fields.configuration = 'validation.configuration';
      else if (cfg.personalization?.names && (!Array.isArray(cfg.personalization.names) || cfg.personalization.names.length > 5000))
        fields.configuration = 'validation.configuration';
    } else if (typeof cfg.containerId !== 'string' || typeof cfg.material !== 'string') fields.configuration = 'validation.configuration';
  }

  if (o.consent !== true) fields.consent = 'validation.consent';
  if (typeof o.website === 'string' && o.website.length > 0) fields.website = 'validation.spam';

  if (Object.keys(fields).length) return { ok: false, fields };

  return {
    ok: true,
    value: {
      schemaVersion: RFQ_SCHEMA_VERSION,
      kind,
      locale: str(o.locale, 8) || 'uk',
      contact,
      order,
      configuration: kind === 'configurator' ? (o.configuration as Configuration) : null,
      productInterest: kind === 'general' ? str(o.productInterest, 60) || null : null,
      comments: str(o.comments, 4000),
      consent: true,
      clientCreatedAt: str(o.clientCreatedAt, 40) || new Date().toISOString(),
    },
  };
}

/** RDK-YYMMDD-XXXX, readable on the phone and unambiguous (no 0/O, 1/I). */
export function makeRfqNumber(date = new Date(), rand: () => number = Math.random): string {
  const alphabet = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  const yy = String(date.getUTCFullYear()).slice(2);
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  let tail = '';
  for (let i = 0; i < 4; i++) tail += alphabet[Math.floor(rand() * alphabet.length)];
  return `RDK-${yy}${mm}${dd}-${tail}`;
}
